import {NgClass, NgIf, UpperCasePipe} from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {
  BUTTON_COLOR,
  BUTTON_SIZE,
  BUTTON_TYPE,
  FV_AVATAR_BORDER,
  FV_AVATAR_SIZE,
  FvAvatarComponent,
  IFvContextMenuOption,
} from '@discocil/fv-components';
import {TranslateService} from '@ngx-translate/core';
import {
  PhraseConfig,
  initializePhraseAppEditor,
} from 'ngx-translate-phraseapp';
import { Observable, first, forkJoin, of, switchMap, tap } from 'rxjs';
import { DeviceAuthService } from 'src/app/auth/services/device-auth/device-auth.service';
import { WaiterAuthService } from 'src/app/auth/services/waiter-auth/waiter-auth.service';
import {
  bluetoothPrinterConnectedFailed,
  bluetoothPrinterConnectedSuccess,
} from 'src/app/core/config/bluetooth-device-snackbar.config';
import {
  terminalConnectedFailed,
  terminalConnectedSuccess,
} from 'src/app/core/config/terminal-snackbar.config';
import { AccountSocketHandlerService } from 'src/app/core/entities/account/account-socket-handler.service';
import { Account } from 'src/app/core/entities/account/domain/model/account.model';
import { AccountStorePort } from 'src/app/core/entities/account/domain/ports/account-store.port';
import { ACCOUNT_STORE } from 'src/app/core/entities/account/domain/tokens/account-store.token';
import { DeviceService } from 'src/app/core/entities/device/device.service';
import { IDevice } from 'src/app/core/entities/device/models/device.model';
import { OrganizationRepositoryAdapter } from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import { GENERAL_PERMISSIONS } from 'src/app/features/permission/domain/enums/general-permissions.enum';
import { PermissionFacade } from 'src/app/features/permission/application/facades/permission.facade';
import { APP_ROUTE_PATH, ERROR_TYPE } from 'src/app/core/enums';
import { ACCOUNT_ROUTE_PATH } from 'src/app/core/enums/routes/account-route-path.enum';
import { SERVICE_ROUTE_PATH } from 'src/app/core/enums/routes/service-route-path.enum';
import { TAILWIND_THEME } from 'src/app/core/enums/tailwind.enum';
import { GlobalErrorResponse } from 'src/app/core/interfaces/global-error.model';
import { IOrganization } from 'src/app/core/entities/organization/domain/interfaces/organization.interface';
import { AppConfigService } from 'src/app/core/services/app-config/app-config.service';
import { BluetoothManagerService } from 'src/app/core/services/bluetooth-manager/bluetooth-manager.service';
import { CleanerService } from 'src/app/core/services/cleaner/cleaner.service';
import { NetworkConnectionService } from 'src/app/core/services/network-connection/network-connection.service';
import { TerminalManagerService } from 'src/app/core/services/terminal-manager/terminal-manager.service';
import {PaymentFlag} from 'src/app/features/payment/domain/model/payment-flags.enum';
import { HEADER_FLAG } from 'src/app/shared/enums/header-flags.enum';
import { FeatureFlagAdapter } from 'src/app/shared/infrastructure/adapters/feature-flag.adapter';
import { GlobalConfirmService } from 'src/app/shared/services/global-confirm/global-confirm.service';
import { GlobalContextMenuService } from 'src/app/shared/services/global-context-menu/global-context-menu.service';
import { GlobalSnackbarService } from 'src/app/shared/services/global-snackbar/global-snackbar.service';
import { ACCOUNT_SIDE_VIEW_TYPE } from 'src/app/shared/services/layout/enum/account-view-type.enum';
import { LayoutService } from 'src/app/shared/services/layout/layout.service';
import { VersionService } from 'src/app/shared/services/version/version.service';
import { environment } from 'src/environments/environment';
import { ENVIRONMENT_NAME } from 'src/environments/environment-name.enum';
import { ClockComponent } from '../../../../components/clock/clock.component';
import { GlobalLogVisualizerService } from '../../../../services/global-log-visualizer/global-log-visualizer.service';
import { EVENT_BUS } from '../../../../domain/tokens/event-bus.token';
import { CAPACITOR_SERVICE } from '../../../../capacitor-service/token/capacitor-service.token';

import { SERVICE_STATE_SERVICE } from '@features/service/domain/tokens/service-state.token';
import { Drawer } from '@features/service/domain/models/drawer.model';
import { ServiceWorkerChangedEvent } from '@platform/event-bus/domain/events/service-worker-changed.event';

/**
 * Header component responsible for managing the application's header section.
 * Handles user interactions with header elements, navigation, and various
 * configuration options like theme switching, device management, and feature flags.
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [FvAvatarComponent, NgIf, NgClass, UpperCasePipe, ClockComponent],
})
export class HeaderComponent implements OnInit {
  private readonly globalContextMenuService = inject(GlobalContextMenuService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly waiterAuthService = inject(WaiterAuthService);
  private readonly deviceAuthService = inject(DeviceAuthService);
  private readonly cleanerServicer = inject(CleanerService);
  private readonly translateService = inject(TranslateService);
  private readonly capacitorService = inject(CAPACITOR_SERVICE);
  private readonly globalLogVisualizerService = inject(
    GlobalLogVisualizerService
  );

  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly deviceService = inject(DeviceService);
  private readonly permissionFacade = inject(PermissionFacade);
  private readonly networkConnectionService = inject(NetworkConnectionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureFlagService = inject(FeatureFlagAdapter);
  private readonly globalConfirmService = inject(GlobalConfirmService);
  private readonly accountSocketHandlerService = inject(
    AccountSocketHandlerService
  );
  private readonly versionService = inject(VersionService);
  private readonly bluetoothService = inject(BluetoothManagerService);
  private readonly globalSnackbarService = inject(GlobalSnackbarService);
  private readonly terminalService = inject(TerminalManagerService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);
  private readonly eventBus = inject(EVENT_BUS);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  organization!: IOrganization | undefined;
  device!: IDevice | null;
  image = '';

  TAILWIND_THEME = TAILWIND_THEME;
  ACCOUNT_VIEW_TYPE = ACCOUNT_SIDE_VIEW_TYPE;
  FV_AVATAR_BORDER = FV_AVATAR_BORDER;
  FV_AVATAR_SIZE = FV_AVATAR_SIZE;
  theme = '';
  version = '';

  dropdownOptions: IFvContextMenuOption[] = [];
  translations = signal<{ [key: string]: string }>({});
  dropdownTranslations = signal<{ [key: string]: string }>({});

  isPhraseEnabled = false;
  isLogActive = false;

  PHRASE_LOCAL_STORAGE_KEY = 'phrase/in-context';
  isDevEnvironment = signal<boolean>(false);
  isHelperActivatedFlag = signal<boolean>(false);
  isInternetConnected = true;

  accountViewType = signal<ACCOUNT_SIDE_VIEW_TYPE | null>(null);
  allowIntegratedPaymentsFlag = signal<boolean>(false);

  canGenerateConsumptions = computed(() => this.permissionFacade.hasPermission(GENERAL_PERMISSIONS.canGenerateConsumptions));
  canUnlinkDevice = computed(() => this.permissionFacade.hasPermission(GENERAL_PERMISSIONS.canUnlinkDevice));
  canConfigurePaymentTerminals = computed(() => this.permissionFacade.hasPermission(GENERAL_PERMISSIONS.canConfigurePaymentTerminals));
  canConfigurePrinters = computed(() => this.permissionFacade.hasPermission(GENERAL_PERMISSIONS.canConfigurePrinters));
  //TODO comment this permission and implement it later
  /*
  canChangeCashDrawer = signal<boolean>(true);
  canChangeLocation = signal<boolean>(true);
  */

  /**
   * Returns the current router URL path
   */
  get routePath(): string {
    return this.router.url;
  }

  /**
   * Determines whether account options can be displayed based on the current route
   * @returns True if account options should be visible, false otherwise
   */
  get canShowAccountOptions(): boolean {
    return ![APP_ROUTE_PATH.serviceConfiguration].some(
      (route: string) => this.routePath.includes(route)
    );
  }

  /**
   * Checks if a drawer is currently selected
   * @returns True if a drawer is selected, false otherwise
   */
  get isCurrentDrawerSelected(): boolean {
    return !!this.serviceStateService.getCurrentDrawer();
  }

  private _isConfigurationOpened = false;

  /**
   * Indicates whether the configuration panel is opened
   */
  get isConfigurationOpened(): boolean {
    return this._isConfigurationOpened;
  }

  /**
   * Checks if mocks are enabled in the application
   */
  get isMocksEnabled(): boolean {
    const mocksData = environment.mocksData ?? {};

    return Object.values(mocksData).some((value) => value === true);
  }


  constructor() {
    this.setFeatureFlags();
    this.isDevEnvironment.set(
      environment.name === ENVIRONMENT_NAME.dev ||
      environment.name === ENVIRONMENT_NAME.alpha
    );
  }

  /**
   * Initializes component data and sets up subscriptions
   */
  ngOnInit(): void {
    this.getHeaderTranslations();
    this.getOrganization();
    this.getDevice();
    this.getVersion();
    this.initializePhraseEditorConfig();
    this.networkConnectionListener();
  }

  /**
   * Initializes the Phrase Editor configuration for translations
   * Only enabled in development environments
   */
  initializePhraseEditorConfig(): void {
    // Load In Context Editor of Phrase for translarions
    if (this.isDevEnvironment()) {
      this.isPhraseEnabled =
        localStorage.getItem(this.PHRASE_LOCAL_STORAGE_KEY) === 'true';
      const config: PhraseConfig = {
        projectId: environment.phraseInContext.projectId,
        accountId: environment.phraseInContext.accountId,
        phraseEnabled: this.isPhraseEnabled,
        fullReparse: true,
      };

      initializePhraseAppEditor(config);
    }
  }

  /**
   * Handles the header dropdown button click
   * Opens the context menu with relevant options or closes it if already open
   * @param event - Mouse event that triggered the action
   */
  onClickHeaderDropdown(event: MouseEvent): void {
    if (this.globalContextMenuService.isOpened) {
      this.globalContextMenuService.close();

      return;
    }

    this.getDropdownTranslations()
      .pipe(
        switchMap(() => this.setHeaderDropdownOptions()),
        first(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.globalContextMenuService.open({
          config: {
            isRight: true,
          },
          options: this.dropdownOptions,
          event,
        });
      });
  }

  /**
   * Handles the configuration dropdown button click
   * Opens the context menu with configuration options
   * @param event - Mouse event that triggered the action
   */
  onClickConfigurationDropdown(event: MouseEvent): void {
    if (this.globalContextMenuService.isOpened) {
      this.globalContextMenuService.close();

      return;
    }

    this.setConfigurationDropdownOptions();
    this.globalContextMenuService.open({
      config: {
        isRight: true,
      },
      options: this.dropdownOptions,
      event,
    });
  }

  /**
   * Navigates to the consumption generation page
   */
  onClickGenerateConsumptions(): void {
    this.router.navigate(
      [
        `${APP_ROUTE_PATH.service}/${SERVICE_ROUTE_PATH.account}/${ACCOUNT_ROUTE_PATH.consumption}`,
      ],
      { replaceUrl: true }
    );
  }

  /**
   * Sets up listener for network connection status changes
   */
  private networkConnectionListener(): void {
    this.networkConnectionService
      .getNetworkConnectionStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isInternetConnected: boolean) => {
        this.isInternetConnected = isInternetConnected;
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Retrieves application version from the version service
   */
  private getVersion(): void {
    const getVersion$ = this.versionService.getVersion().pipe(
      first(),
      tap((version) => {
        this.version = version?.version
          ? `Fourvenues POS v${version?.version}`
          : '';
      })
    );

    this.versionService.onVersionChanged$
      .pipe(
        switchMap(() => getVersion$),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Retrieves the current organization data
   */
  private getOrganization(): void {
    this.organizationService
      .getCurrentOrganization()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((organization: IOrganization | null) => {
        if (organization) {
          this.organization = organization;
          this.image = this.organization?.imagen;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  /**
   * Retrieves the current device data
   */
  private getDevice(): void {
    this.deviceService
      .getDevice()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((device: IDevice | null) => {
        this.device = device;
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Sets up dropdown options for the header menu
   * Combines multiple option groups based on user permissions and system state
   * @returns Observable that completes when all options are configured
   */
  private setHeaderDropdownOptions(): Observable<unknown> {
    return forkJoin({
      drawers: of(this.serviceStateService.getAllDrawers()),
      currentDrawer: of(this.serviceStateService.getCurrentDrawer()),
    }).pipe(
      first(),
      tap(({ drawers, currentDrawer }) => {
        this.dropdownOptions = [];

        type IFvContextMenuOptions = (() => void)[];

        const currentAccount = this.accountStoreService.getSelectedAccount();

        const drawerBlock: IFvContextMenuOptions = [
          this.addCurrentDrawerOption.bind(this, {
            drawers,
            currentDrawer,
            currentAccount: currentAccount ?? null,
          }),
        ];

        const printerBlock: IFvContextMenuOptions = [
          this.addConnectPrinterOption.bind(this),
          this.addConnectTerminalOption.bind(this),
        ];

        const consumptionsBlock: IFvContextMenuOptions = [
          this.addGenerateConsumptionsOption.bind(this),
        ];

        const themeBlock: IFvContextMenuOptions = [
          this.addToggleTheme.bind(this),
          this.addSideViewOption.bind(this),
        ];

        const reloadBlock: IFvContextMenuOptions = [
          this.addReloadOption.bind(this),
        ];

        const unlinkBlock: IFvContextMenuOptions = [
          this.addUnlinkWaiterOption.bind(this),
          this.addUnlinkDevice.bind(this),
        ];

        const versionBlock: IFvContextMenuOptions = [
          this.addDeviceVersion.bind(this),
        ];

        const blocks: IFvContextMenuOptions[] = [
          drawerBlock,
          printerBlock,
          consumptionsBlock,
          themeBlock,
          reloadBlock,
          unlinkBlock,
          versionBlock,
        ];

        let previousBlockAdded = false;

        blocks.forEach((block, index) => {
          const previousLength = this.dropdownOptions.length;

          block.forEach((optionFn) => optionFn());
          const currentBlockAdded = this.dropdownOptions.length > previousLength;

          if (index > 0 && previousBlockAdded && currentBlockAdded) {
            const hasSubsequentBlocksWithContent = blocks.slice(index + 1).some((subsequentBlock) => {
              const testLength = this.dropdownOptions.length;

              subsequentBlock.forEach((optionFn) => optionFn());

              const subsequentAdded = this.dropdownOptions.length > testLength;

              this.dropdownOptions.splice(testLength);

              return subsequentAdded;
            });

            if (hasSubsequentBlocksWithContent) {
              this.dropdownOptions.splice(previousLength, 0, {
                isHorizontalRule: true,
              });
            }
          }

          previousBlockAdded = currentBlockAdded;
        });
      })
    );
  }

  /**
   * Adds device unlinking option to dropdown menu if user has permission
   */
  private addUnlinkDevice(): void {
    if (!this.canUnlinkDevice()) {
      return;
    }

    this.dropdownOptions.push({
      icon: 'far fa-mobile-android-alt',
      title: this.dropdownTranslations()['dropdown_menu.unlink_device'],
      handler: this.openUnlinkDeviceConfirm.bind(this),
    });
  }

  /**
   * Adds device version information to dropdown menu
   */
  private addDeviceVersion(): void {
    this.dropdownOptions.push({
      isDisabled: true,
      class: 'pointer-events-none !text-2xs opacity-30',
      title: this.version,
      handler: () => void 0,
    });
  }

  /**
   * Adds printer connection option to dropdown menu if feature flag is enabled
   */
  private addConnectPrinterOption(): void {
    if (!this.canConfigurePrinters()) {
      return;
    }

    this.dropdownOptions.push({
      icon: 'fas fa-print',
      title: this.dropdownTranslations()['global.printers'],
      handler: () => {
        this.bluetoothService
          .loadPrinterModal()
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe((isConnected: boolean | null) => {
            if (isConnected) {
              this.globalSnackbarService.open(bluetoothPrinterConnectedSuccess);

              return;
            }

            this.globalSnackbarService.open(bluetoothPrinterConnectedFailed);
          });
      },
    });
  }

  /**
   * Adds terminal connection option to dropdown menu if integrated payments are enabled
   */
  private addConnectTerminalOption(): void {
    if (!this.allowIntegratedPaymentsFlag() || !this.canConfigurePaymentTerminals()) {
      return;
    }

    this.dropdownOptions.push({
      icon: 'fas fa-mobile-signal-out',
      title: this.dropdownTranslations()['global.terminals'],
      handler: () => {
        this.terminalService
          .loadTerminalModal()
          .pipe(first(), takeUntilDestroyed(this.destroyRef))
          .subscribe((isConnected: boolean | null) => {
            if (!isConnected && isConnected !== null) {
              this.globalSnackbarService.open(terminalConnectedFailed);
            }

            if (isConnected) {
              this.globalSnackbarService.open(terminalConnectedSuccess);

              return;
            }
          });
      },
    });
  }

  /**
   * Adds consumption generation option to dropdown menu if conditions are met
   */
  private addGenerateConsumptionsOption(): void {
    if (
      this.serviceStateService.getCurrentWorker() &&
      !this.layoutService.isMobile &&
      this.canGenerateConsumptions() &&
      this.isCurrentDrawerSelected
    ) {
      this.dropdownOptions.push({
        icon: 'far fa-ticket-alt',
        title: this.dropdownTranslations()['dropdown_menu.generate_consumption'],
        handler: this.onClickGenerateConsumptions.bind(this),
      });
    }
  }

  /**
   * Adds waiter sign out option to dropdown menu if a waiter is logged in
   */
  private addUnlinkWaiterOption(): void {
    if (this.serviceStateService.getCurrentWorker()) {
      this.dropdownOptions.push({
        icon: 'far fa-sign-out',
        title: this.dropdownTranslations()['dropdown_menu.unlink_waiter'],
        handler: this.signOutWaiter.bind(this),
      });
    }
  }

  /**
   * Adds drawer selection option to dropdown menu
   */
  private addCurrentDrawerOption(params: {
    drawers: Drawer[];
    currentDrawer: Drawer | null;
    currentAccount: Account | null;
  }): void {
    const { drawers, currentDrawer, currentAccount } = params;

    if (
      this.canShowAccountOptions &&
      currentDrawer &&
      currentAccount
      //TODO comment this permission and implement it later
      //&&
      //this.canChangeCashDrawer()
    ) {
      const drawerColor = `text-${currentDrawer?.color}-500 dark:text-${currentDrawer?.color}-500`;
      const changeDrawerDisabled = currentDrawer.locationIds.length < 2 && drawers.length < 2;

      this.dropdownOptions.push({
        icon: `fas fa-circle text-2xl ${drawerColor}`,
        title: currentDrawer.name,
        isDisabled: changeDrawerDisabled,
        handler: () => {
          this.changeCashDrawer();
        },
      });
    }
  }

  /**
   * Adds application reload option to dropdown menu
   */
  private addReloadOption(): void {
    this.dropdownOptions.push({
      icon: 'fas fa-rotate',
      title: this.dropdownTranslations()['dropdown_menu.reload'],
      handler: () => document.location.reload(),
    });
  }

  /**
   * Adds side view toggle option to dropdown menu
   */
  private addSideViewOption(): void {
    this.accountViewType.set(this.layoutService.accountSideView);
    this.dropdownOptions.push({
      icon:
        this.accountViewType() === ACCOUNT_SIDE_VIEW_TYPE.left
          ? 'fas fa-right-to-bracket'
          : 'fas fa-left-to-bracket',
      title:
        this.accountViewType() === ACCOUNT_SIDE_VIEW_TYPE.left
          ? this.dropdownTranslations()['dropdown_menu.right_view']
          : this.dropdownTranslations()['dropdown_menu.left_view'],
      handler: this.onClickChangeAccountViewType.bind(this),
    });
  }

  private addToggleTheme(): void {
    const isLightTheme = this.layoutService.isLightTheme;

    this.dropdownOptions.push({
      icon: isLightTheme ? 'far fa-moon' : 'far fa-sun',
      title: isLightTheme
        ? this.dropdownTranslations()['dropdown_menu.dark_mode']
        : this.dropdownTranslations()['dropdown_menu.light_mode'],
      handler: this.onClickToggleTheme.bind(this),
    });
  }

  private setConfigurationDropdownOptions(): void {
    const dropdownOptions = [
      {
        icon: this.isPhraseEnabled ? 'fas fa-language' : 'far fa-language',
        title: this.isPhraseEnabled ? 'Hide Phrase' : 'Show Phrase',
        handler: this.onClickInContextPhrase.bind(this),
      },
      {
        icon: this.isLogActive ? 'fas fa-list' : 'far fa-list',
        title: 'Show Log',
        handler: this.onClickLog.bind(this),
      },
      {
        icon: environment.isTrackSubscriptionsEnabled
          ? 'fas fa-clock'
          : 'far fa-clock',
        title: environment.isTrackSubscriptionsEnabled
          ? 'Hide timers'
          : 'Show timers',
        handler: this.onClickTrackSubscriptions.bind(this),
      },
      {
        icon: environment.isSocketsEnabled ? 'fas fa-plug' : 'far fa-plug',
        title: environment.isSocketsEnabled
          ? 'Disable sockets'
          : 'Enable sockets',
        handler: this.onClickSocketEnabled.bind(this),
      },
      {
        icon: this.isMocksEnabled ? 'fas fa-brush' : 'far fa-brush',
        title: this.isMocksEnabled ? 'Disable mocks' : 'Enable mocks',
        handler: this.onClickToggleMocks.bind(this),
      },
      {
        icon: 'fas fa-circle-info',
        title: 'Go to helper',
        handler: (): void => {
          this.router.navigate([APP_ROUTE_PATH.healthCheck], {
            replaceUrl: true,
          });
        },
      },
    ];

    this.dropdownOptions = dropdownOptions;
  }

  /**
   * Navigates to the drawer page if certain conditions are met:
   * - Retrieves the current account from IndexedDB.
   * - Retrieves the drawers from IndexedDB.
   * - Checks if the current account has entries or if there is only one drawer available.
   * - Navigates to the drawer page if the conditions are met.
   */
  private changeCashDrawer(): void {
    this.accountSocketHandlerService.sendOpenAccountsSyncEvent();
    this.serviceStateService.changeDrawer();
  }

  /**
   * Unpairs the current device, clears local storage and navigates to login
   */
  private unpairDevice(): void {
    this.deviceAuthService
      .signOutDevice()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          localStorage.clear();
          this.cleanerServicer.clearAllData();
          this.router.navigate([''], { replaceUrl: true });
        },
      });
  }

  /**
   * Signs out the current waiter and navigates to waiter login page
   */
  private signOutWaiter(): void {
    this.waiterAuthService
      .signOutWaiter()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.eventBus.publish(new ServiceWorkerChangedEvent());
        },
        error: (error: GlobalErrorResponse) => {
          if (error.message.includes(ERROR_TYPE.offline)) {
            this.eventBus.publish(new ServiceWorkerChangedEvent());
          }
        },
      });
  }

  private getDropdownTranslations(): Observable<{ [key: string]: string }> {
    return this.translateService
      .get([
        'dropdown_menu.unlink_device',
        'dropdown_menu.unlink_waiter',
        'dropdown_menu.right_view',
        'dropdown_menu.left_view',
        'dropdown_menu.reload',
        'dropdown_menu.generate_consumption',
        'global.printers',
        'global.terminals',
        'dropdown_menu.dark_mode',
        'dropdown_menu.light_mode',
      ])
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        tap((translations: { [key: string]: string }) => {
          this.dropdownTranslations.set(translations);
          this.changeDetectorRef.markForCheck();
        })
      );
  }

  private getHeaderTranslations(): void {
    this.translateService
      .get(['fv_confirm.unlink_device.title', 'global.unlink', 'global.cancel'])
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((translations: { [key: string]: string }) => {
        this.translations.set(translations);
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * Sets a new theme for the application
   * @param theme - The theme to apply
   */
  setNewTheme(theme: TAILWIND_THEME): void {
    document.getElementsByTagName('html')[0].className = theme;

    const bgColor =
      theme === TAILWIND_THEME.dark
        ? TAILWIND_THEME.bgDarkColor
        : TAILWIND_THEME.bgLightColor;

    this.capacitorService.setBackgroundColor(bgColor);

    this.changeDetectorRef.markForCheck();
  }

  /**
   * Toggles the Phrase in-context editor and reloads the application
   */
  onClickInContextPhrase(): void {
    this.isPhraseEnabled = !this.isPhraseEnabled;
    localStorage.setItem(
      this.PHRASE_LOCAL_STORAGE_KEY,
      this.isPhraseEnabled.toString()
    );
    document.location.reload();
  }

  /**
   * Toggles between light and dark themes
   */
  private onClickToggleTheme(): void {
    this.layoutService.changeTheme();
  }

  /**
   * Opens the log visualizer
   */
  private onClickLog(): void {
    this.globalLogVisualizerService.open();
  }

  /**
   * Toggles subscription tracking for debugging
   */
  private onClickTrackSubscriptions(): void {
    environment.isTrackSubscriptionsEnabled =
      !environment.isTrackSubscriptionsEnabled;
  }

  /**
   * Toggles socket connection functionality
   */
  private onClickSocketEnabled(): void {
    environment.isSocketsEnabled = !environment.isSocketsEnabled;
  }

  /**
   * Toggles mock data usage
   */
  private onClickToggleMocks(): void {
    if (environment.name === ENVIRONMENT_NAME.prod) {
      return;
    }

    this.appConfigService.changeUseMocksConfig(!this.isMocksEnabled);
  }

  /**
   * Changes the account view type (left/right)
   */
  private onClickChangeAccountViewType(): void {
    this.layoutService.changeAccountViewType();
  }

  /**
   * Sets up feature flags and initializes permissions
   */
  private setFeatureFlags(): void {
    this.featureFlagService.onFeatureFlagsLoaded$
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.isHelperActivatedFlag.set(
          this.featureFlagService.isFlagActivated(HEADER_FLAG.allowHealthHelper)
        );

        this.allowIntegratedPaymentsFlag.set(
          this.featureFlagService.isFlagActivated(
            PaymentFlag.ALLOW_INTEGRATED_PAYMENTS
          )
        );
      });
  }

  /**
   * Opens confirmation dialog for unlinking device
   */
  private openUnlinkDeviceConfirm(): void {
    this.globalConfirmService.open({
      noClose: true,
      icon: 'fas fa-exclamation-circle',
      iconSize: 'text-3xl ',
      message: this.translations()['fv_confirm.unlink_device.title'],
      hideCloseIcon: true,
      buttons: [
        {
          text: this.translations()['global.unlink'],
          color: BUTTON_COLOR.error,
          size: BUTTON_SIZE.full,
          type: BUTTON_TYPE.filled,
          preventAutoClose: true,
          handler: (): void => {
            this.globalConfirmService.close();
            this.unpairDevice();
          },
        },
        {
          text: this.translations()['global.cancel'],
          color: BUTTON_COLOR.secondary,
          size: BUTTON_SIZE.full,
          type: BUTTON_TYPE.filled,
          preventAutoClose: true,
          handler: (): void => {
            this.globalConfirmService.close();
          },
        },
      ],
    });
  }

}
